import React, {
    Children,
    FunctionComponent,
    PropsWithChildren,
    useEffect,
    useRef,
} from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useHasEnteredViewport } from '../../hooks/dom';

interface LazyRendererNodeProps extends RouteComponentProps<any> {
    initialRender?: boolean;
}

const LazyRendererNode = withRouter(
    (props: PropsWithChildren<LazyRendererNodeProps>) => {
        const { initialRender, history, children } = props;
        const ref = useRef(null);
        const { hasEnteredViewport, reset } = useHasEnteredViewport(ref);
        const shouldRender = hasEnteredViewport || initialRender;

        useEffect(() => {
            const unlisten = history.listen(() => {
                reset();
            });

            return () => unlisten();
        }, [history, reset]);

        return (
            <div
                className="lazy-render-proxy"
                ref={ref}
                style={{
                    height: shouldRender ? 'auto' : '400px',
                }}
            >
                {shouldRender ? children : null}
            </div>
        );
    },
);

interface LazyRendererProps {
    /**
     * Zero indexed number representing
     * number of children to initially
     * render.
     *
     * If set to -1 will render all
     * children synchronously
     */
    numChildInitialRender?: number;
}

export const LazyRenderer: FunctionComponent<LazyRendererProps> = (props) => {
    const { numChildInitialRender = 0, children } = props;

    if (!children) {
        return null;
    }

    return (
        <>
            {Children.map(children, (child, index) => {
                return (
                    <LazyRendererNode
                        initialRender={
                            numChildInitialRender === -1
                                ? true
                                : index <= numChildInitialRender
                        }
                    >
                        {child}
                    </LazyRendererNode>
                );
            })}
        </>
    );
};
